// Keep a draft through Horizon section replacement; never put fiscal values in
// localStorage, analytics, logs or an order note. Only the two owned attributes
// are patched, leaving notes, discounts and Sendcloud attributes alone.
const requestKey = 'Richiesta fattura';
const vatKey = 'Partita IVA';
let draft;
let verified;
let queue = Promise.resolve();

class GomitoloCartInvoice extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-invoice-toggle]');
    this.request = this.querySelector(`input[name="attributes[${requestKey}]"]`);
    this.vat = this.querySelector(`input[name="attributes[${vatKey}]"]`);
    this.status = this.querySelector('[data-invoice-status]');
    if (!this.toggle || !this.request || !this.vat || !this.status) return;
    // Initialize once from server-rendered attributes. null means an earlier
    // request had an uncertain result; stale section HTML cannot clear that.
    if (verified === undefined) verified = { [requestKey]: this.request.value, [vatKey]: this.vat.value };
    if (draft) {
      this.toggle.checked = draft[requestKey] === 'Sì';
      this.vat.value = draft[vatKey];
    }
    this.toggle.disabled = false;
    this.sync();
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.addEventListener('input', () => this.sync(), options);
    this.addEventListener('change', () => {
      this.sync();
      if (this.vat.checkValidity()) void this.save().catch(() => this.showError());
    }, options);
    document.addEventListener('submit', event => this.checkout(event), { ...options, capture: true });
  }

  disconnectedCallback() { this.controller?.abort(); }

  sync() {
    this.request.value = this.toggle.checked ? 'Sì' : 'No';
    this.vat.required = this.toggle.checked;
    this.querySelector('[data-invoice-fields]').hidden = !this.toggle.checked;
    if (!this.toggle.checked) this.vat.value = '';
    this.vat.setCustomValidity(this.toggle.checked && !this.vat.value.trim() ? this.dataset.invalidMessage : '');
    draft = { [requestKey]: this.request.value, [vatKey]: this.vat.value };
  }

  showError() { this.status.textContent = this.dataset.errorMessage; }

  save() {
    const attributes = { ...draft };
    const operation = async () => {
      // Check inside the queue: an older save may still be in flight. Untouched
      // or already saved values need no extra network request before checkout.
      if (verified?.[requestKey] === attributes[requestKey] && verified?.[vatKey] === attributes[vatKey]) return;
      verified = null;
      const response = await fetch(this.dataset.updateUrl, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      });
      if (!response.ok) throw new Error('Invoice cart save failed');
      const cart = await response.json();
      if (cart.attributes?.[requestKey] !== attributes[requestKey] ||
          (cart.attributes?.[vatKey] ?? '') !== attributes[vatKey]) throw new Error('Invoice cart save unverified');
      verified = attributes;
    };
    // Do not abort a sent mutation: its outcome could be unknown. Serialize saves
    // so an earlier reply cannot restore a VAT value the customer just removed.
    queue = queue.catch(() => {}).then(operation);
    return queue;
  }

  async checkout(event) {
    if (event.target !== this.request.form || event.submitter?.name !== 'checkout' || this.replaying) return;
    event.preventDefault();
    if (this.submitting) return;
    this.sync();
    if (!this.vat.reportValidity()) return;
    this.submitting = true;
    const button = event.submitter;
    button.disabled = true;
    this.status.textContent = this.dataset.savingMessage;
    try {
      // Inputs may change while a request is in flight. Save and verify the
      // latest draft before submitting the native cart form to Shopify.
      let saved;
      do {
        saved = JSON.stringify(draft);
        await this.save();
        // A verified no-op resolves in the original submit event's microtasks.
        // Leave that event before requestSubmit; browsers reject a reentrant
        // submit while its first dispatch is still in progress.
        await new Promise(resolve => setTimeout(resolve, 0));
      } while (saved !== JSON.stringify(draft));
      if (!this.isConnected) return;
      this.sync();
      if (!this.vat.reportValidity()) return;
      button.disabled = false;
      this.replaying = true;
      this.request.form.requestSubmit(button);
    } catch { this.showError(); }
    finally { this.submitting = false; this.replaying = false; button.disabled = false; }
  }
}

if (!customElements.get('gomitolo-cart-invoice')) customElements.define('gomitolo-cart-invoice', GomitoloCartInvoice);
